import { Backdrop } from '@mui/material';
import AppContainer from 'components/app-container';
import UPLoadingSpinner from 'components/loading-spinner';
import { ConfigurationDomain } from 'constants/ConfigurationDomain';
import { CompanyRole } from 'constants/CompanyRole';
import SetManagerPassword from 'modules/auth/SetManagerPassword';
import SetPassword from 'modules/auth/SetPassword';
import { MfaEnrollmentBanner } from 'modules/auth/mfa-enrollment';
import Availabilities from 'modules/availabilities/employee-availabilities/Availabilities';
import Blogs from 'modules/blogs';
import Company from 'modules/companies';
import Companies from 'modules/companies/Companies';
import EditCompanyInformation from 'modules/companies/company-information/EditCompanyInformation';
import EditContact from 'modules/companies/contact/EditContact';
import EditIntegration from 'modules/companies/integration/EditIntegration';
import { Locations } from 'modules/companies/locations';
import { Managers } from 'modules/companies/managers';
import EditPaymentInformation from 'modules/companies/payment-information/EditPaymentInformation';
import { EditPayroll } from 'modules/companies/payroll';
import CompanyTimeRegistration from 'modules/companies/time-registration';
import { ChildCompaniesStructure } from 'modules/companies/structure';
import Vendors from 'modules/companies/vendors/Vendors';
import CompanyPickScreen from 'modules/company-pick-screen';
import { Contracts } from 'modules/contracts';
import Dashboard from 'modules/dashboard';
import Documents from 'modules/documents';
import DocumentPreview from 'modules/documents/DocumentPreview';
import Employees from 'modules/employees/Employees';
import ForgotPassword from 'modules/forgotpassword/ForgotPassword';
import Logout from 'modules/logout';
import Management from 'modules/management';
import SecurityAuthentication from 'modules/management/security-authentication';
import DomainEmptyState from 'modules/management/DomainEmptyState';
import TimeRegistrationPolicy from 'modules/management/time-registration-policy/TimeRegistrationPolicy';
import AbsenceReasons from 'modules/management/absence';
import JointCommissions from 'modules/management/joint-commissions';
import FeatureAccess from 'modules/management/feature-access';
import ContractCompliance from 'modules/management/contract-compliance';
import { DEFAULT_CONTRACT_COMPLIANCE_TAB } from 'modules/management/contract-compliance/routes';
import ParitairComiteDetails from 'modules/paritair-comite/ParitairComiteDetails';
import PlanningRedirect from 'modules/planning/PlanningRedirect';
import ChangePassword from 'modules/profile/ChangePassword';
import SetPincode from 'modules/profile/SetPincode';
import Profile from 'modules/profile/Profile';
import Reporting from 'modules/reporting';
import AccountManagers from 'modules/superadmins/AccountManagers';
import ApiUsers from 'modules/superadmins/ApiUsers';
import SuperAdmins from 'modules/superadmins/SuperAdmins';
import Users from 'modules/superadmins/Users';
import Translations from 'modules/translations/Translations';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import postAuthStartup from 'services/post-auth-startup';
import { getAnalyticsService } from 'services/analytics';
import { useAppSelector } from 'store';
import { appGeneralLoadingSelector } from 'store/selectors/app-selectors';
import { hasSelectedCompanySelector, profileSelector } from 'store/selectors/profile-selectors';
import MobileRedirectWrapper from './MobileRedirectWrapper';
import RequireRole from './RequireRole';

const AppRouter = (): JSX.Element => {
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  const requestsLoading = useAppSelector(appGeneralLoadingSelector);
  const profile = useAppSelector(profileSelector);
  const selectedCompanyForMCEmployee = useAppSelector(hasSelectedCompanySelector);
  const shouldShowCompanySelectionScreen = profile
    ? profile.role === CompanyRole.EMPLOYEE && profile.userCompanies?.length > 1 && !selectedCompanyForMCEmployee
    : false;

  useEffect(() => {
    (async () => {
      await postAuthStartup();
      setIsReady(true);
    })();
  }, []);

  // ─── Analytics: page view ─────────────────────────────────────────────────
  // Fires only on pathname changes — query-param/tab/sort changes are ignored.
  useEffect(() => {
    if (!isReady) return;
    getAnalyticsService().trackPageView(location.pathname);
  }, [location.pathname, isReady]); // intentionally excludes location.search / location.hash
  // ─────────────────────────────────────────────────────────────────────────

  if (!isReady) {
    return null;
  }

  if (
    shouldShowCompanySelectionScreen &&
    (location.pathname === '/dashboard' ||
      location.pathname === '/contracts' ||
      location.pathname === '/employees' ||
      location.pathname === '/availabilities' ||
      location.pathname === '/docs')
  ) {
    return <Navigate to="/company-selection" state={{ from: location.pathname }} replace />;
  }

  return (
    <AppContainer>
      <Backdrop open={requestsLoading} sx={{ zIndex: 9000, color: '#fff' }} transitionDuration={300}>
        <UPLoadingSpinner />
      </Backdrop>
      {/* Above the routes rather than inside a page: the invitation applies to the whole session, and
          this renders only once `postAuthStartup` has loaded the profile the flag lives on. */}
      <MfaEnrollmentBanner />
      <Routes>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/pincode" element={<SetPincode />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/auth/set-password/:uniqueKey" element={<SetPassword />} />
        <Route path="/auth/set-manager-password/:uniqueKey" element={<SetManagerPassword />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/company/:companyId" element={<Company />}>
          <Route path="general" element={<EditCompanyInformation />} />
          <Route path="contact" element={<EditContact />} />
          <Route path="locations" element={<Locations />} />
          <Route path="payroll" element={<EditPayroll />} />
          <Route path="time-registration" element={<CompanyTimeRegistration />} />
          <Route path="managers" element={<Managers />} />
          <Route path="vendors" element={<Vendors />} />
          <Route path="financial" element={<EditPaymentInformation />} />
          <Route path="integration" element={<EditIntegration />} />
          <Route path="structure" element={<ChildCompaniesStructure />} />
        </Route>
        <Route path="/company/new" element={<Company />}>
          <Route path="general" element={<EditCompanyInformation />} />
          <Route path="contact" element={<EditContact />} />
          <Route path="locations" element={<Locations />} />
          <Route path="payroll" element={<EditPayroll />} />
          <Route path="managers" element={<Managers />} />
          <Route path="vendors" element={<Vendors />} />
          <Route path="financial" element={<EditPaymentInformation />} />
          <Route path="integration" element={<EditIntegration />} />
        </Route>
        <Route path="/company/new" element={<Company />} />

        <Route
          path="/settings/management"
          element={
            <RequireRole roles={[CompanyRole.ADMIN]}>
              <Management />
            </RequireRole>
          }
        >
          <Route index element={<Navigate to={ConfigurationDomain.FEATURE_ACCESS} replace />} />
          <Route path={ConfigurationDomain.FEATURE_ACCESS} element={<FeatureAccess />} />
          <Route path={ConfigurationDomain.ABSENCE} element={<AbsenceReasons />} />
          <Route path={ConfigurationDomain.TIME_REGISTRATION_POLICY} element={<TimeRegistrationPolicy />} />
          <Route path={ConfigurationDomain.CONTRACT_VALIDATION_COMPLIANCE}>
            <Route index element={<Navigate to={DEFAULT_CONTRACT_COMPLIANCE_TAB} replace />} />
            <Route path="*" element={<ContractCompliance />} />
          </Route>
          <Route path={`${ConfigurationDomain.SECURITY_AUTHENTICATION}/*`} element={<SecurityAuthentication />} />
          <Route path={ConfigurationDomain.JOINT_COMMISSIONS}>
            <Route index element={<JointCommissions />} />
            <Route path="paritair-comite-details/:paritairComiteId/*" element={<ParitairComiteDetails />} />
            <Route path="paritair-comite-details/new/*" element={<ParitairComiteDetails />} />
            <Route path="*" element={<JointCommissions />} />
          </Route>
          <Route path=":domainKey/*" element={<DomainEmptyState />} />
        </Route>

        <Route path="/employees/*" element={<Employees />} />
        <Route path="/employees/edit/:pCompanyId/:pEmployeeId" element={<Employees />} />
        <Route path="/employees/:pCompanyId/:pEmployeeName" element={<Employees />} />
        <Route path="/availabilities" element={<Availabilities />} />
        <Route path="/users" element={<Users />}>
          <Route path="superadmins" element={<SuperAdmins />} />
          <Route path="apiUsers" element={<ApiUsers />} />
          <Route path="accountmanagers" element={<AccountManagers />} />
        </Route>
        <Route path="/contracts/*" element={<Contracts />} />
        <Route path="/contracts/:pCompanyId/:pYear/:pWeek" element={<Contracts />} />
        <Route path="/contracts/edit/:pCompanyId/:pContractId" element={<Contracts />} />
        <Route path="/contracts/edit/:pContractId" element={<Contracts />} />
        <Route path="/contracts/confirm/:pCompanyId/:pContractDateId" element={<Contracts />} />
        <Route
          path="/document-preview/:id"
          element={<MobileRedirectWrapper Component={DocumentPreview} redirectPath="/employees-app" />}
        />
        <Route
          path="/planning"
          element={<MobileRedirectWrapper Component={PlanningRedirect} redirectPath="/employees-app" />}
        />
        <Route path="/docs" element={<Documents />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/forgotpassword" element={<ForgotPassword />} />
        <Route path="/reports" element={<Reporting />} />
        <Route path="/translations" element={<Translations />} />
        <Route path="*" element={<Navigate to="/dashboard" />} />
        <Route path="/company-selection" element={<CompanyPickScreen />} />
      </Routes>
    </AppContainer>
  );
};

export default AppRouter;
